import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { DownloadSimple, Lightning, Fingerprint, LockSimple, ShieldCheck, QrCode } from "@phosphor-icons/react";
import useLatestApk from "@/hooks/useLatestApk";
import { BACKEND_URL, PACKAGE_NAME, SOCIAL_PROOF_DOWNLOADS } from "@/lib/site";
import { formatBytes, formatDate, releasedAt } from "@/lib/format";
import useT from "@/i18n";

const Meta = ({ label, value, testId }) => (
  <div className="rounded-xl p-3.5 stat-tile min-w-0">
    <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase">{label}</div>
    <div className="text-sm font-semibold mt-1 break-words" data-testid={testId}>{value}</div>
  </div>
);

export const DownloadCard = ({ headingLevel = "h2" }) => {
  const { apk, loading } = useLatestApk();
  const { t: T } = useT();
  const t = T.card;
  const [showQr, setShowQr] = useState(false);
  const H = headingLevel;
  const url = apk ? `${BACKEND_URL}${apk.download_url}` : null;

  return (
    <div className="relative">
      <div className="absolute -inset-4 rounded-[32px] blur-2xl opacity-40" style={{ background: "radial-gradient(circle at 40% 30%, #00BFFF, transparent 60%)" }} aria-hidden="true" />
      <article className="relative glass rounded-3xl p-5 xs:p-7 sm:p-10 glow-cyan-sm" data-testid="apk-download-card" aria-label={t.cardAria}>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <span className="badge-pill mb-3">{t.latest}</span>
            <H className="font-display text-4xl sm:text-5xl font-black tracking-tighter" data-testid="apk-version">
              {loading ? "…" : apk ? `v${apk.version_name}` : t.soon}
            </H>
            {apk?.force_update && (
              <span className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full text-xs font-semibold bg-red-500/15 text-red-300 border border-red-400/30" data-testid="apk-force-badge">
                <Lightning size={12} weight="fill" aria-hidden="true" /> {t.force}
              </span>
            )}
          </div>
          <div className="text-right">
            <div className="text-xs tracking-[0.2em] text-slate-500 uppercase">{t.downloads}</div>
            <div className="font-display text-2xl font-bold text-[#7DDCFF]" data-testid="download-count">{SOCIAL_PROOF_DOWNLOADS}</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 xs:gap-3 mb-6">
          <Meta label={t.released} value={formatDate(releasedAt(apk), T.dateLocale)} testId="apk-date" />
          <Meta label={t.size} value={formatBytes(apk?.size)} testId="apk-size" />
          <Meta label={t.requires} value={t.android(apk ? apk.min_android : "7.0")} testId="apk-android" />
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href={url || undefined}
            aria-disabled={!url}
            className="btn-primary flex-1 !py-4 text-base"
            data-testid="apk-download-btn"
            aria-label={apk ? t.aria(apk.version_name) : t.ariaNone}
            download
          >
            <DownloadSimple size={22} weight="bold" aria-hidden="true" />
            {apk ? t.dl : t.none}
          </a>
          <button type="button" disabled={!url} onClick={() => setShowQr((s) => !s)} className="btn-ghost" aria-expanded={showQr} aria-controls="apk-qr" data-testid="apk-qr-toggle">
            <QrCode size={20} aria-hidden="true" />
            <span className="hidden md:inline">{t.scan}</span>
            <span className="md:hidden">{showQr ? t.hideQr : t.showQr}</span>
          </button>
        </div>

        <AnimatePresence>
          {showQr && url && (
            <motion.div id="apk-qr" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden" data-testid="apk-qr-panel">
              <div className="mt-5 flex flex-col xs:flex-row items-center gap-5 p-5 rounded-2xl" style={{ background: "rgba(0,0,0,.3)", border: "1px solid rgba(0,191,255,.18)" }}>
                <div className="bg-white p-3 rounded-xl shrink-0">
                  <QRCodeSVG value={url} size={148} level="M" title={t.qrTitle(apk.version_name)} data-testid="apk-qr-code" />
                </div>
                <p className="text-sm text-slate-300 leading-relaxed text-center xs:text-left">
                  {t.qrText(apk.version_name)}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <p className="text-center text-xs text-slate-500 mt-3">{t.official}</p>

        {apk?.release_notes && (
          <div className="mt-6 pt-6 border-t" style={{ borderColor: "rgba(255,255,255,.06)" }}>
            <div className="text-[10px] tracking-[0.2em] text-slate-500 uppercase mb-2">{t.notes}</div>
            <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed break-words" data-testid="apk-release-notes">{apk.release_notes}</p>
          </div>
        )}

        <div className="mt-6 grid sm:grid-cols-2 gap-3 text-xs">
          <div className="flex items-center gap-2 text-slate-400 min-w-0">
            <Fingerprint size={14} className="text-[#00BFFF] shrink-0" aria-hidden="true" /> {t.pkg} <span className="font-mono-alt text-slate-300 truncate">{PACKAGE_NAME}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <LockSimple size={14} className="text-[#00BFFF]" aria-hidden="true" /> {t.signed}
          </div>
        </div>

        {apk?.sha256 && (
          <details className="mt-4">
            <summary className="text-xs text-slate-500 cursor-pointer hover:text-[#7DDCFF] flex items-center gap-2" data-testid="apk-sha256-toggle">
              <ShieldCheck size={12} aria-hidden="true" /> {t.sha}
            </summary>
            <div className="mt-2 font-mono-alt text-[11px] text-slate-400 break-all p-3 rounded-lg" style={{ background: "rgba(0,0,0,.35)", border: "1px solid rgba(255,255,255,.05)" }} data-testid="apk-sha256">
              {apk.sha256}
            </div>
          </details>
        )}
      </article>
    </div>
  );
};
